/// <reference types="@types/webappsec-credential-management" />
import { Injectable } from '@angular/core';
import { switchMap, map } from 'rxjs/operators';
import { Observable, from, of } from 'rxjs';
import base64url from 'base64url';
import WebAuthnService, { UserCredential } from './webauthn.service';
import MemoryAdapter from './memory-adapter.service';
import { AssertionChallenge } from './assertion-challenge-builder.service';
import { LoggingService } from '../logging.service';

export interface StatusResponse {
  status: string;
  message?: string;
}

export interface Credential {
  username: string;
  password: string;
}

@Injectable({
  providedIn: 'root',
})
export class CredentialsService {
  constructor(
    private readonly webAuthnSvc: WebAuthnService,
    private readonly memoryAdapter: MemoryAdapter,
    private readonly logger: LoggingService,
  ) {}

  public setUserCredential(cred: Credential | null): void {
    if (cred) {
      this.memoryAdapter.putCred(cred.username, cred.password).catch((error) => {
        this.logger.error(error);
      });
    }
  }

  public async getUserCredential(): Promise<Credential | null> {
    return this.memoryAdapter.getCred();
  }

  public registerUser(user: UserCredential): Observable<StatusResponse | null> {
    let session: {
      challenge: string | Buffer | undefined;
      name: string;
      loggedIn?: boolean;
    };
    return from(this.webAuthnSvc.register(user)).pipe(
      switchMap(async (response) => {
        session = response.session;
        return this.preformatMakeCredReq(response);
      }),
      switchMap((publicKey) => {
        publicKey.challenge = base64url.toBuffer(publicKey.challenge as string);
        publicKey.user.id = base64url.toBuffer(publicKey.user.id as string);
        return navigator.credentials.create({ publicKey: publicKey as unknown as PublicKeyCredentialCreationOptions });
      }),
      switchMap((credResponse) => {
        return this.sendWebauthnResponse(credResponse, session);
      }),
    );
  }

  public loginUser(username: string): Observable<StatusResponse | null> {
    let session:
      | {
          challenge: string | Buffer | undefined;
          name: string;
          loggedIn?: boolean;
        }
      | undefined;
    return from(this.webAuthnSvc.login(username)).pipe(
      switchMap(async (response) => {
        session = response.session;
        const publicKey = this.preformatGetAssertReq(response);
        publicKey.challenge = base64url.toBuffer(publicKey.challenge as string);
        publicKey.allowCredentials.map((cred: { id: string | Buffer }) => {
          cred.id = base64url.toBuffer(cred.id as string);

          return cred;
        });
        return navigator.credentials.get({ publicKey: publicKey as unknown as PublicKeyCredentialCreationOptions });
      }),
      switchMap((result) => this.sendWebauthnResponse(result, session)),
    );
  }

  private sendWebauthnResponse(
    cred: CredentialType | null,
    session: { challenge: string | Buffer | undefined; name: string; loggedIn?: boolean } | undefined,
  ): Observable<StatusResponse | null> {
    if (!session) {
      return of(null);
    }
    const makeCredResponse = this.publicKeyCredentialToJSON(cred);
    return from(this.webAuthnSvc.response(makeCredResponse, session)).pipe(
      map((result) => {
        if (result.status === 'ok') {
          session.loggedIn = true;
        }

        return result;
      }),
    );
  }

  private publicKeyCredentialToJSON(pubKeyCred: any): any {
    if (Array.isArray(pubKeyCred)) {
      return pubKeyCred.map((i) => this.publicKeyCredentialToJSON(i));
    }
    if (pubKeyCred instanceof ArrayBuffer) {
      return base64url.encode(pubKeyCred as Buffer);
    }
    if (pubKeyCred instanceof Object) {
      const obj: any = {};
      for (const key in pubKeyCred) {
        if (pubKeyCred[key]) {
          obj[key] = this.publicKeyCredentialToJSON(pubKeyCred[key]);
        }
      }
      return obj;
    }

    return pubKeyCred;
  }

  private preformatMakeCredReq(makeCredReq: {
    challenge: string | Buffer | undefined;
    user: { id: string | Buffer };
  }): {
    challenge: string | Buffer | undefined;
    user: {
      id: string | Buffer;
    };
  } {
    if (typeof makeCredReq.challenge === 'string') {
      makeCredReq.challenge = base64url.decode(makeCredReq.challenge);
    }
    if (typeof makeCredReq.user.id === 'string') {
      makeCredReq.user.id = base64url.decode(makeCredReq.user.id);
    }

    return makeCredReq;
  }

  private preformatGetAssertReq(getAssert: AssertionChallenge): AssertionChallenge {
    getAssert.challenge = base64url.decode(getAssert.challenge as string);
    for (const allowCred of getAssert.allowCredentials) {
      allowCred.id = base64url.decode(allowCred.id as string);
    }
    return getAssert;
  }
}
